import { AnyStateMachine, assign } from 'xstate';
import { ApplicationContext } from './applicationMachine';
import {
  ApplicationMachineEvents,
  SendAssignGeneratedDisclosuresParams,
  SendAssignSelectedOfferParams,
  SendChildMachineReadyParams,
} from './types/events';
import { FraudAlloyMachineActorRefLogic } from './machines/fraudAlloy/useFraudAlloyMachineLogic';
import { estimatedSpendMachine } from './machines/estimatedSpend/estimatedSpendMachine';
import {
  ApplicationCheckoutResult,
  ApplicationPrepareCheckoutResult,
  CreateApplicationResult,
  FetchApplicationResult,
  FetchLatestApplicationResult,
  PrepareInStoreApplicationResult,
  ReCreateApplicationResult,
  ReCreateInStoreContingentApplicationResult,
} from './types/actors';
import { PaymentsMachineActorRefLogic } from './machines/payments/usePaymentsMachineLogic';

/**
 * Assigns selected offer to context for event `SEND_ASSIGN_SELECTED_OFFER`
 */
export const assignSelectedOffer = assign<
  ApplicationContext,
  ApplicationMachineEvents,
  SendAssignSelectedOfferParams,
  ApplicationMachineEvents,
  never
>({
  selectedOffer: (_, event) => {
    return event.selectedOffer;
  },
});

/**
 * Resets selected offer to null for event `SEND_RESET_SELECTED_OFFER`
 */
export const resetSelectedOffer = assign<
  ApplicationContext,
  ApplicationMachineEvents,
  unknown,
  ApplicationMachineEvents,
  never
>({
  selectedOffer: () => null,
});

/**
 * Assigns application to context for event `SEND_FETCH_LATEST_APPLICATION`
 */
export const assignApplication = assign<
  ApplicationContext,
  ApplicationMachineEvents,
  | FetchLatestApplicationResult
  | FetchApplicationResult
  | CreateApplicationResult
  | ReCreateApplicationResult
  | ApplicationCheckoutResult
  | ApplicationPrepareCheckoutResult
  | PrepareInStoreApplicationResult
  | ReCreateInStoreContingentApplicationResult,
  ApplicationMachineEvents,
  never
>({
  application: (_, params) => {
    return params.applicationRes.result;
  },
});

/**
 * Assigns application with appended status codes to context for event `SEND_CREATE_APPLICATION`
 */
export const assignApplicationWithAppendedStatusCodes = assign<
  ApplicationContext,
  ApplicationMachineEvents,
  | FetchLatestApplicationResult
  | CreateApplicationResult
  | ReCreateApplicationResult
  | ApplicationCheckoutResult
  | ApplicationPrepareCheckoutResult
  | ReCreateInStoreContingentApplicationResult,
  ApplicationMachineEvents,
  never
>({
  application: ({ context }, params) => {
    const { application } = context;
    const applicationResult = params.applicationRes.result;

    if (!applicationResult) {
      return null;
    }
    return {
      ...applicationResult,
      statusCodes: [
        ...(application?.statusCodes || []),
        ...(applicationResult?.statusCodes || []),
      ],
    };
  },
});

/**
 * Assigns generatedDisclosures to context for event `SEND_ASSIGN_GENERATED_DISCLOSURES`
 */
export const assignGeneratedDisclosures = assign<
  ApplicationContext,
  ApplicationMachineEvents,
  SendAssignGeneratedDisclosuresParams,
  ApplicationMachineEvents,
  never
>({
  generatedDisclosures: (_, params) => {
    return params.generatedDisclosures;
  },
});

/**
 * Assigns stateMachineActorRefs to context on initialization
 */
export const assignStateMachineActorRefs = assign<
  ApplicationContext,
  ApplicationMachineEvents,
  unknown,
  ApplicationMachineEvents,
  never
>({
  stateMachineActorRefs: () => {
    throw new Error('Not implemented');
  },
});

/**
 * Factory that returns an `assignStateMachineActorRefs` action with the
 * spawned child machine actor refs assigned to context.
 */
export const createAssignStateMachineActorRefs = ({
  fraudAlloyMachineLogic,
  paymentsMachineLogic,
}: {
  fraudAlloyMachineLogic: FraudAlloyMachineActorRefLogic;
  paymentsMachineLogic: PaymentsMachineActorRefLogic;
}): typeof assignStateMachineActorRefs =>
  assign<
    ApplicationContext,
    ApplicationMachineEvents,
    unknown,
    ApplicationMachineEvents,
    never
  >({
    stateMachineActorRefs: ({ spawn }) => {
      return {
        fraudAlloyMachineActorRef: spawn(fraudAlloyMachineLogic),
        paymentsMachineActorRef: spawn(paymentsMachineLogic, {
          input: {
            paymentMethods: [],
            selectedPaymentMethodID: null,
          },
        }),
        estimatedSpendMachineActorRef: spawn(
          estimatedSpendMachine as AnyStateMachine
        ),
      };
    },
  });

/**
 * Assigns stateMachineActorRefs to context on initialization
 */
export const assignChildMachineReady = assign<
  ApplicationContext,
  ApplicationMachineEvents,
  SendChildMachineReadyParams,
  ApplicationMachineEvents,
  never
>({
  childMachinesReady: ({ context }, params) => {
    const newSet = new Set(context.childMachinesReady);
    newSet.add(params.machineId);
    return newSet;
  },
});

/**
 * Assigns deferredInitializationEvents to context on initialization
 */
export const assignDeferredInitializationEvent = assign<
  ApplicationContext,
  ApplicationMachineEvents,
  ApplicationMachineEvents,
  ApplicationMachineEvents,
  never
>({
  deferredInitializationEvents: ({ context, event }) => {
    return [...context.deferredInitializationEvents, event];
  },
});

/**
 * Clears deferredInitializationEvents after initialization
 */
export const clearDeferredInitializationEvents = assign<
  ApplicationContext,
  ApplicationMachineEvents,
  unknown,
  ApplicationMachineEvents,
  never
>({
  deferredInitializationEvents: () => {
    return [];
  },
});

/**
 * Resets context to null for event `SEND_RESET_APPLICATION_STATE`
 */
export const resetContext = assign<
  ApplicationContext,
  ApplicationMachineEvents,
  undefined,
  ApplicationMachineEvents,
  never
>({
  application: null,
  selectedOffer: null,
  generatedDisclosures: null,
});
